import { observer } from "mobx-react-lite";
import React, { useEffect, useState } from "react";
import { useMst } from "../../../store/root";
import styles from "./CompositionItem.module.scss";
import { theme } from "../../../theme";
import { FilteredComposition } from "../CompositionList/CompositionList";
import lottie, { AnimationItem } from "lottie-web";
import spinner from "../../../assets/animations/spinner.json";
import { currentStatusType } from "../../../helpers/renderViewHelper";
import {
  CompositionStatus,
  IconStatus,
} from "../CompositionList/CompositionStatus";

const { primary, background2, teal_80 } = theme;

type Props = {
  item: FilteredComposition;
};
/**
 * Jsx for each composition
 * Option 1: Not in a rendering state
 * Option 2: In a rendering state
 * @param item
 * @returns Jsx
 */
const CompositionItem = observer(({ item }: Props) => {
  const {
    compositionStore: {
      setSelectedCompositions,
      renderedCompositionsFail,
      selectedCompositionsId,
      queuedCompositions,
      renderedCompositionsSuccess,
      compositionCurrentlyRendering,
      trigger,
    },
    renderStore: { isRendering },
    uiStore: { filterComposition },

    projectStore: { videoParts: scenes },
  } = useMst();

  const [hover, setHover] = React.useState(false);
  const currentScene = scenes?.find((scene) => scene.name === item.name);
  // State that keeps track of the loading status of the compositions
  const [compositionStatus, setCompositionStatus] =
    useState<currentStatusType>("notRendering");

  // Updates the loading status of the compositions
  useEffect(() => {
    const loadingStatus = ((): currentStatusType =>
      renderedCompositionsFail.some((comp) => item.id === comp)
        ? "fail"
        : renderedCompositionsSuccess.some((comp) => item.id === comp)
        ? "success"
        : queuedCompositions.some((comp) => item.id === comp) && isRendering
        ? compositionCurrentlyRendering.id === item.id
          ? "loading"
          : "waiting"
        : "notRendering")();
    setCompositionStatus(loadingStatus);
  }, [
    compositionCurrentlyRendering.id,
    renderedCompositionsSuccess,
    renderedCompositionsFail,
    queuedCompositions,
    trigger,
  ]);

  // Timer is present to avoid triggering the animation before .spinner.active is created
  useEffect(() => {
    let timer: any = null;
    let loaderAnimation: AnimationItem;
    if (compositionCurrentlyRendering.id !== 0 && !timer) {
      timer = setTimeout(() => {
        const loaderContainer = document.querySelector(".spinner.active");
        if (loaderContainer && loaderContainer.children.length === 0) {
          loaderAnimation = lottie.loadAnimation({
            container: loaderContainer,
            renderer: "svg",
            loop: true,
            autoplay: true,
            animationData: spinner, // the path to the animation json
          });          
        }
      }, 500);
    }
    return () => {
      if (timer) clearTimeout(timer); //Clear timer to avoid creating multiple timers simultaneously
      loaderAnimation?.destroy();
    };
  }, [compositionCurrentlyRendering.id]);

  return (
    <div
      key={item.id}
      onClick={() => !isRendering && setSelectedCompositions(item.id)}
      className={styles.ListItem}
      onMouseEnter={(e) => setHover(true)}
      onMouseLeave={(e) => setHover(false)}
    >
      <div className={styles.Left}>
        <IconStatus
          state={compositionStatus}
          isSelected={selectedCompositionsId.some((id) => id === item.id)}
          hasFilter={filterComposition}
          isRendering={isRendering}
        />
        <p
          className={[
            styles.Title,
            (filterComposition || isRendering) &&
            !selectedCompositionsId.some((comp) => comp === item.id)
              ? styles.Disabled
              : "",
          ].join(" ")}
        >
          {item.name}
        </p>
      </div>
      <div className={styles.Right}>
        <CompositionStatus
          status={compositionStatus}
          hover={hover}
          composition={item}
          currentScene={currentScene}
        />
      </div>
    </div>
  );
});

export default CompositionItem;
